// skipcq: JS-W1028, JS-0128
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Tabs, Tab } from '@mui/material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import TabPanel from '../../components/tabs/TabPanel';
import MetricDefinitionsTab from './MetricDefinitionsTab';
import MetricImagesTab from './MetricImagesTab';
import useMetricDefinitionsList from '../../hooks/useMetricDefinitionsList';
import useMetricStorageImages from '../../hooks/useMetricStorageImages';
import EditMetricDialog from './EditMetricDialog';
import { validateMetricKey } from '../../domain/metricConstants';

function defaultImageForList(allowedImages) {
  if (allowedImages?.length) {
    return allowedImages[0];
  }
  return '';
}

function emptyForm(allowedImages) {
  return {
    key: '',
    name: '',
    helpText: '',
    appMapping: '',
    sortOrder: 0,
    image: defaultImageForList(allowedImages),
  };
}

function metricToForm(metric, allowedImages) {
  const rawImage = (metric.image ?? '').trim();
  const image = allowedImages.includes(rawImage)
    ? rawImage
    : defaultImageForList(allowedImages);
  return {
    key: metric.id ?? metric.key ?? '',
    name: metric.name ?? '',
    helpText: metric.helpText ?? '',
    appMapping: metric.appMapping ?? '',
    sortOrder:
      metric.sortOrder === undefined || metric.sortOrder === null
        ? 0
        : metric.sortOrder,
    image,
  };
}

/**
 * Metrics management: definitions in Firestore `metricDefinitions`, images in Storage `metrics/`.
 */
function Metrics() {
  const { metrics, loading, error, createMetric, updateMetric } =
    useMetricDefinitionsList();
  const {
    files: storageFiles,
    fileNames,
    imageUrlByName,
    loading: storageLoading,
    error: storageError,
    refresh: refreshStorage,
    uploadFile,
    deleteImageIfUnused,
  } = useMetricStorageImages();

  const allowedImageChoices = useMemo(
    () => [...fileNames].sort((a, b) => a.localeCompare(b)),
    [fileNames],
  );

  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [form, setForm] = useState(() => emptyForm([]));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!dialogOpen) return;
    if (!allowedImageChoices.length) return;
    const trimmedImageChoice = (form.image ?? '').trim();
    if (allowedImageChoices.includes(trimmedImageChoice)) return;
    setForm((prev) => ({
      ...prev,
      image: defaultImageForList(allowedImageChoices),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit form.image: run when dialog or choices change, not on every image keystroke
  }, [dialogOpen, allowedImageChoices]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleOpenAddDialog = useCallback(() => {
    setErrorMsg('');
    setDialogMode('add');
    setForm(emptyForm(allowedImageChoices));
    setDialogOpen(true);
  }, [allowedImageChoices]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setErrorMsg('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value, type: inputType } = event.target;
    if (name === 'sortOrder') {
      const n = inputType === 'number' ? Number(value) : parseInt(value, 10);
      setForm((prev) => ({
        ...prev,
        sortOrder: Number.isFinite(n) ? n : 0,
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleEdit = useCallback(
    (metric) => {
      setErrorMsg('');
      setDialogMode('edit');
      setForm(metricToForm(metric, allowedImageChoices));
      setDialogOpen(true);
    },
    [allowedImageChoices],
  );

  const handleSubmitMetric = useCallback(async () => {
    const name = form.name?.trim();
    const image = form.image?.trim();
    if (!name) {
      setErrorMsg('Please enter a name.');
      return;
    }
    if (!image || !allowedImageChoices.includes(image)) {
      setErrorMsg(
        'Choose an image from the list, or upload one on the Metric images tab.',
      );
      return;
    }

    if (dialogMode === 'add') {
      const keyResult = validateMetricKey(form.key);
      if (!keyResult.ok) {
        setErrorMsg(keyResult.message);
        return;
      }
    }

    setSubmitting(true);
    setErrorMsg('');
    try {
      if (dialogMode === 'edit') {
        await updateMetric(form.key, {
          name,
          image,
          helpText: form.helpText,
          appMapping: form.appMapping,
          sortOrder: form.sortOrder,
        });
      } else {
        await createMetric({
          key: form.key,
          name,
          image,
          helpText: form.helpText,
          appMapping: form.appMapping,
          sortOrder: form.sortOrder,
        });
      }
      setDialogOpen(false);
      setForm(emptyForm(allowedImageChoices));
    } catch (err) {
      const message =
        err?.message && typeof err.message === 'string'
          ? err.message
          : dialogMode === 'edit'
            ? 'Failed to save metric. Please try again.'
            : 'Failed to create metric. Please try again.';
      setErrorMsg(message);
    } finally {
      setSubmitting(false);
    }
  }, [
    allowedImageChoices,
    createMetric,
    dialogMode,
    form,
    updateMetric,
  ]);

  return (
    <DashboardLayout title="Metrics">
      <Container fixed>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Metrics section tabs"
          >
            <Tab
              id="simple-tab-0"
              aria-controls="simple-tabpanel-0"
              label="Metric definitions"
            />
            <Tab
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
              label="Metric images"
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <MetricDefinitionsTab
            loading={loading}
            error={error}
            onOpenAdd={handleOpenAddDialog}
            metrics={metrics}
            imageUrlByName={imageUrlByName}
            onEdit={handleEdit}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <MetricImagesTab
            files={storageFiles}
            loading={storageLoading}
            error={storageError}
            refresh={refreshStorage}
            uploadFile={uploadFile}
            deleteImageIfUnused={deleteImageIfUnused}
          />
        </TabPanel>
      </Container>
      <EditMetricDialog
        open={dialogOpen}
        mode={dialogMode}
        submitting={submitting}
        errorMsg={errorMsg}
        form={form}
        availableImages={allowedImageChoices}
        imagesLoading={storageLoading}
        onClose={handleCloseDialog}
        onChange={handleFormChange}
        onSubmit={handleSubmitMetric}
      />
    </DashboardLayout>
  );
}

export default Metrics;
